import { StatusId, TabKind, TabDescriptor, CommandeBC } from '../types';

// Logique pure des onglets de la case PRESTATAIRE x BC. Onglets FIXES (le presta ne
// voit QUE son rang : pas de cascade de rangs comme cote acheteur). Gating 100% id-based.

// Statut effectif de la commande (table `status` partagee), lu par id.
const statusId = (commande: CommandeBC): number | undefined =>
  commande.status?.id ?? commande.id_status;

// Statuts ou l'onglet Offre est cliquable cote presta. Conversion id-based du
// `prestataireCanAccessOffre` legacy (qui matchait des libelles FR, avec une confusion
// connue : le dev visait "apres envoi de l'offre" = AttenteAnalyse(13), mais matchait la
// chaine de PropositionFaite(15)). On couvre l'intention : decision (3,4,5,6), chiffrage
// panier inverse (11), offre envoyee / proposition (13,15) et etats terminaux (7,8,9).
const OFFRE_ENABLED_STATUS: ReadonlySet<number> = new Set<number>([
  StatusId.AttenteAcceptationRang1, // 3
  StatusId.AAccepter, // 4
  StatusId.AttenteAcceptationRang2, // 5
  StatusId.AttenteAcceptationRang3, // 6
  StatusId.Valide, // 7
  StatusId.Refuse, // 8
  StatusId.Expire, // 9
  StatusId.AChiffrer, // 11
  StatusId.AttenteAnalyse, // 13
  StatusId.PropositionFaite, // 15
]);

const isOffreEnabled = (commande: CommandeBC): boolean => {
  const sid = statusId(commande);
  return sid !== undefined && OFFRE_ENABLED_STATUS.has(sid);
};

// Ordre des onglets = stable (cle de ?tab=<index>). Messagerie + Evaluation : inactifs
// "a venir" (decision produit), comme la case acheteur BC.
const TAB_DEFS: { kind: TabKind; label: string }[] = [
  { kind: 'historique', label: 'Historique' },
  { kind: 'consultation', label: 'Consultation' },
  { kind: 'offre', label: 'Offre' },
  { kind: 'notification', label: 'Notification et signature' },
  { kind: 'messagerie', label: 'Messagerie' },
  { kind: 'evaluation', label: 'Évaluation' },
];

// L'onglet Evaluation se deverrouille une fois la commande validee (acceptee/signee = 7).
const isValide = (commande: CommandeBC): boolean => statusId(commande) === StatusId.Valide;

export const buildCommandeTabs = (commande: CommandeBC): TabDescriptor[] => {
  const offreDisabled = !isOffreEnabled(commande);
  const evalDisabled = !isValide(commande);
  // Messagerie privee : active (non "a venir"), grisee tant que la commande n'est pas validee (7).
  const messagerieDisabled = !isValide(commande);
  // Notification et signature : accessible UNIQUEMENT quand le bon de commande est accepte/signe des
  // deux cotes (statut Valide=7). Grisee avant (aucune signature a montrer), comme l'Evaluation.
  const notificationDisabled = !isValide(commande);
  return TAB_DEFS.map((def) => ({
    key: def.kind,
    kind: def.kind,
    label: def.label,
    disabled:
      def.kind === 'offre'
        ? offreDisabled
        : def.kind === 'evaluation'
          ? evalDisabled
          : def.kind === 'messagerie'
            ? messagerieDisabled
            : def.kind === 'notification'
              ? notificationDisabled
              : false,
    inactive: false,
  }));
};

// Onglet d'ouverture par defaut (hors ?tab=) :
// - chiffrage panier inverse (Brouillon presta / A chiffrer) -> Consultation
// - sinon, si l'Offre est accessible -> Offre (la ou se joue la decision/le suivi)
// - sinon -> Historique.
export const computeDefaultTabKind = (commande: CommandeBC): TabKind => {
  const sid = statusId(commande);
  if (sid === StatusId.BrouillonPrestataire || sid === StatusId.AChiffrer) return 'consultation';
  if (isOffreEnabled(commande)) return 'offre';
  return 'historique';
};

export const computeDefaultTabIndex = (
  descriptors: TabDescriptor[],
  commande: CommandeBC,
): number => {
  const kind = computeDefaultTabKind(commande);
  const index = descriptors.findIndex((d) => d.kind === kind && !d.disabled && !d.inactive);
  return index >= 0 ? index : 0;
};
